import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { useSession } from '../../state/useSession';
import { useProjects } from '../../state/projects';
import { isWebApp } from '@/webapp/webAppRecord';
import type { AppDefinition } from '@/types/app';
import ChatPanel from './ChatPanel';
import PreviewFrame from './PreviewFrame';
import PublishPanel from './PublishPanel';
import UnsupportedRuntime from './UnsupportedRuntime';
import { Link } from 'react-router';

/**
 * Chat and preview.
 *
 * Below 1024px they are two views with a toggle, as on the phone. From 1024px both are
 * on screen at once and the toggle disappears rather than being disabled — the extra
 * width buys a capability instead of rearranging the same one.
 */
export default function ProjectEditor({ mode }: { mode?: 'chat' | 'preview' }) {
  const { id = '' } = useParams();
  const { user } = useSession();
  const [params] = useSearchParams();
  const project = useProjects((s) => s.currentProject);
  const [view, setView] = useState<'chat' | 'preview'>(mode ?? (params.get('mode') === 'preview' ? 'preview' : 'chat'));

  useEffect(() => {
    void useProjects.getState().loadProject(id, user?.id ?? '');
  }, [id, user?.id]);

  if (!project || project.id !== id) return <p className="type-body">Loading…</p>;

  const definition = project.definition as AppDefinition;
  if (!isWebApp(definition)) return <UnsupportedRuntime runtime={definition.runtime ?? 'engine'} />;

  const source = definition.source ?? '';

  async function applySource(next: string): Promise<void> {
    await useProjects.getState().updateProject(id, { definition: { ...definition, source: next } }, user?.id ?? '');
  }

  return (
    <div className="editor">
      <header className="editor-head">
        <h1 className="type-title">{project.name}</h1>
        <div className="editor-toggle" role="tablist" aria-label="Editor view">
          <button
            type="button"
            role="tab"
            aria-selected={view === 'chat'}
            className={view === 'chat' ? 'is-selected' : ''}
            onClick={() => setView('chat')}
          >
            Chat
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={view === 'preview'}
            className={view === 'preview' ? 'is-selected' : ''}
            onClick={() => setView('preview')}
          >
            Preview
          </button>
        </div>
      </header>

      <div className="editor-split">
        <div className={view === 'chat' ? 'editor-pane' : 'editor-pane is-hidden-narrow'}>
          <ChatPanel
            projectId={id}
            appId={definition.hostedAppId ?? definition.id}
            appName={project.name}
            goal={project.description}
            source={source}
            onSource={applySource}
          />
        </div>
        <div className={view === 'preview' ? 'editor-pane' : 'editor-pane is-hidden-narrow'}>
          <PreviewFrame name={project.name} source={source} />
        </div>
      </div>

      {/*
        * Outside the panes, so it is reachable in BOTH views. A build lands here in
        * preview mode, and while publishing lived inside the chat pane it was
        * display:none at phone width — the app was finished and there was no way to put
        * it on the internet without knowing to switch tabs first.
        */}
      <PublishPanel project={project} />
      <p className="type-body editor-links">
        <Link to={`/submissions/${id}`}>See replies people sent</Link>
      </p>
    </div>
  );
}
